import { useCallback, useEffect, useState } from 'react'
import { MessageSquare } from 'lucide-react'
import { SidebarEmptyState } from '@/components/layout/sidebar/SidebarEmptyState'
import { SidebarErrorState } from '@/components/layout/sidebar/SidebarErrorState'
import { SidebarListSkeleton } from '@/components/layout/sidebar/SidebarListSkeleton'
import { fetchWithAuth } from '@/lib/api/core'
import { formatRelativeTime } from '@/lib/format-relative-time'
import { resolveOpenExternalIcon } from '@/lib/open-destination-icons'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'
import { testIds } from '@/lib/test-ids'
import { decodeDriveCommentHtml } from './drive-comment-text'

const DriveOpenIcon = resolveOpenExternalIcon(GoogleDriveIcon)

export type DrivePreviewComment = {
  id: string
  content: string
  htmlContent?: string
  author?: { displayName: string; photoLink?: string }
  createdTime?: string
  resolved?: boolean
  quotedFileContent?: { mimeType?: string; value?: string }
}

export function DriveCommentsList({
  fileId,
  organizationId,
}: {
  fileId: string
  organizationId?: string
}) {
  const [comments, setComments] = useState<DrivePreviewComment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = organizationId
        ? `?organizationId=${encodeURIComponent(organizationId)}`
        : ''
      const res = await fetchWithAuth(
        `/api/google-drive/files/${encodeURIComponent(fileId)}/comments${params}`,
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(typeof body?.error === 'string' ? body.error : `HTTP ${res.status}`)
      }
      const data = (await res.json()) as { comments?: DrivePreviewComment[] }
      setComments(data.comments ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comments')
    } finally {
      setLoading(false)
    }
  }, [fileId, organizationId])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) return <SidebarListSkeleton rows={4} dense />
  if (error) return <SidebarErrorState message={error} onRetry={load} />
  if (comments.length === 0) {
    return (
      <SidebarEmptyState
        icon={<MessageSquare className="h-6 w-6" />}
        title="No comments"
        description="Open comments on this document appear here."
      />
    )
  }

  return (
    <div data-testid={testIds.driveSidebar.drivePreviewCommentsPanel}>
      {comments.map((comment) => {
        const discoUrl = `https://docs.google.com/document/d/${fileId}/edit?disco=${comment.id}`
        const quotedText = decodeDriveCommentHtml(
          comment.quotedFileContent?.value?.trim() ?? '',
        )
        const body = decodeDriveCommentHtml(comment.content ?? '')
        return (
          <div
            key={comment.id}
            className="space-y-3 border-b border-border/30 px-3 py-3.5"
            data-testid={testIds.driveSidebar.drivePreviewCommentItem(comment.id)}
          >
            {quotedText ? (
              <div className="rounded border-l-2 border-primary/40 bg-primary/5 px-2.5 py-2">
                <p className="line-clamp-2 text-sm italic leading-relaxed text-muted-foreground">
                  "{quotedText}"
                </p>
              </div>
            ) : null}
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-sm font-medium">
                    {comment.author?.displayName ?? 'Unknown'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatRelativeTime(comment.createdTime)}
                  </span>
                </div>
                <p className="mt-1 text-sm leading-relaxed text-foreground">
                  {body}
                </p>
              </div>
              <a
                href={discoUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open comment in Google Docs"
                className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <DriveOpenIcon className="h-4 w-4" />
              </a>
            </div>
          </div>
        )
      })}
    </div>
  )
}
